'use client';

import React, { createContext, use, useEffect, useId } from 'react';

import { Collapsible, CollapsibleContent } from '../ui/collapsible';
import { cn } from '../utils/cn';
import { getOrganizationAssets } from '../utils/organizations';

const translations: Record<'sv' | 'en' | 'no' | 'dk' | 'fi' | 'de', Record<string, string>> = {
  sv: {
    free: 'Gratis',
    before: 'Innan',
    between: 'Mellan',
    from: 'Från',
    deliveryTime: 'Leveranstid',
    workdays: 'arbetsdagar',
    days: 'dagar',
    hours: 'timmar',
    weeks: 'veckor',
  },
  en: {
    free: 'Free',
    before: 'Before',
    between: 'Between',
    from: 'From',
    deliveryTime: 'Delivery time',
    workdays: 'weekdays',
    days: 'days',
    hours: 'hours',
    weeks: 'weeks',
  },
  no: {
    free: 'Gratis',
    before: 'Før',
    between: 'Mellom',
    from: 'Fra',
    deliveryTime: 'Leveringstid',
    workdays: 'arbeidsdager',
    days: 'dager',
    hours: 'timer',
    weeks: 'uker',
  },
  dk: {
    free: 'Gratis',
    before: 'Før',
    between: 'Mellem',
    from: 'Fra',
    deliveryTime: 'Leveringstid',
    workdays: 'arbejdsdage',
    days: 'dage',
    hours: 'timer',
    weeks: 'uger',
  },
  fi: {
    free: 'Ilmainen',
    before: 'Ennen',
    between: 'Välillä',
    from: 'Alkaen',
    deliveryTime: 'Toimitusaika',
    workdays: 'työpäivää',
    days: 'päivää',
    hours: 'tuntia',
    weeks: 'viikkoa',
  },
  de: {
    free: 'Kostenlos',
    before: 'Vor',
    between: 'Zwischen',
    from: 'Ab',
    deliveryTime: 'Lieferzeit',
    workdays: 'Arbeitstage',
    days: 'Tage',
    hours: 'Stunden',
    weeks: 'Wochen',
  },
};

function formatPrice(priceInCents: number, currency: string, locale: string) {
  const localeContent = translations[locale as keyof typeof translations] ?? translations.en;

  // special handling of free delivery
  if (priceInCents === 0) {
    return localeContent.free;
  }

  return `${(priceInCents / 100).toLocaleString(locale, {
    minimumFractionDigits: ['SEK', 'NOK', 'DKK'].includes(currency) ? 0 : 2,
    maximumFractionDigits: ['SEK', 'NOK', 'DKK'].includes(currency) ? 0 : 2,
  })} ${currency}`;
}

/**
 * Helper to safely format either a string or Date into a localized date.
 */
function formatToDateString(value: string | Date, locale: string): string {
  // If it is already a Date, we'll use it directly.
  // If it's a string, we try constructing a Date from it.
  const date = value instanceof Date ? value : new Date(value);

  // If the date is invalid, fallback to the original string.
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  // You can tweak the options here (e.g., weekday, month, day, etc.)
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short', // e.g., "Wed"
    day: 'numeric', // e.g., "23"
    month: 'short', // e.g., "Dec"
    // year: 'numeric',   // uncomment if you need the year too
  }).format(date);
}

interface DeliveryTime {
  interval?: {
    start: number;
    end: number;
    type: 'workdays' | 'days' | 'hours' | 'weeks';
  };
  latest?: string | Date;
  earliest?: string | Date;
  overrideString?: string;
}

export function formatDeliveryTime(time: DeliveryTime, locale: string): string {
  const { interval, earliest, latest, overrideString } = time;

  const localeContent = translations[locale as keyof typeof translations] ?? translations.en;

  // 0) If we have an override string, use that directly
  if (overrideString) {
    return overrideString;
  }

  // Prepare formatted dates:
  const earliestFormatted = earliest ? formatToDateString(earliest, locale) : '';
  const latestFormatted = latest ? formatToDateString(latest, locale) : '';

  // 1) If we have an interval with earliest & latest numeric values
  //    e.g. "2–5 days"
  if (interval) {
    const { start, end, type } = interval;
    return `${start}–${end} ${localeContent[type]}`;
  }

  // 3) If we only have a "latest" time, show "before Wed 23 Dec"
  if (latest && !earliest) {
    return `${localeContent.before} ${latestFormatted}`;
  }

  // 4) If we only have an "earliest" time, show "from Wed 23 Dec"
  if (earliest && !latest) {
    return `${localeContent.from} ${earliestFormatted}`;
  }

  // 5) If we have both "earliest" and "latest", show "from Wed 23 Dec before Fri 25 Dec"
  if (earliest && latest) {
    return `${localeContent.between} ${earliestFormatted} - ${latestFormatted}`;
  }

  // 5) If none of the above apply, just return an empty string or some fallback
  return '';
}

/* -----------------------------------------------------------------------------------------------*/

interface GroupContextType {
  name: string;
  value?: string;
  onValueChange: (value: string) => void;
  locale?: string;
  currency?: string;
}

/**
 * This context holds data for the group of options
 */
const GroupContext = createContext<GroupContextType>({
  name: 'delivery-option',
  onValueChange: () => {},
});

export interface DeliveryOptionsGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  name?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  locale?: string;
  currency?: string;
}

/**
 * DeliveryOptionsGroup represents a list of delivery options that can be selected.
 *
 * It provides a context for each delivery option to know of the state of the group, such as which value is selected.
 *
 * See tests for usage examples.
 */
export function DeliveryOptionsGroup(props: DeliveryOptionsGroupProps) {
  const { name, defaultValue, onValueChange, locale, currency, ...rest } = props;

  const defaultName = useId();

  const [groupContextValue, setGroupContextValue] = React.useState<GroupContextType>({
    name: name ?? defaultName,
    value: defaultValue,
    onValueChange: (value: string) => {
      setGroupContextValue((prev) => ({ ...prev, value }));
      onValueChange?.(value);
    },
    locale: locale ?? 'en',
    currency: currency ?? 'EUR',
  });

  useEffect(() => {
    // If locale or currency changes, sync them in the context
    setGroupContextValue((prev) => ({
      ...prev,
      locale,
      currency,
    }));
  }, [locale, currency]);

  return (
    <GroupContext.Provider value={groupContextValue}>
      <div {...rest} />
    </GroupContext.Provider>
  );
}

/* -----------------------------------------------------------------------------------------------*/

interface DeliveryOptionProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The value of the option. Will be sent to the `onValueChange` callback of the `DeliveryOptionsGroup` when selected.
   */
  value: string;
}

interface OptionContextType {
  value: string;
  isChecked: boolean;
}

/**
 * This context holds data for each indivudal option.
 */
const OptionContextType = createContext<OptionContextType>({
  value: '',
  isChecked: false,
});

/**
 * DeliveryOption represents a single delivery option that can be selected.
 *
 * It should be used inside a `DeliveryOptionsGroup` component.
 */
export function DeliveryOption(props: DeliveryOptionProps) {
  const { children, className, value, ...rest } = props;

  const groupContext = use(GroupContext);

  const isChecked = groupContext.value === value;

  const deliveryOptionContextValue = {
    value,
    isChecked,
  };

  return (
    <OptionContextType.Provider value={deliveryOptionContextValue}>
      <div
        className={cn('flex flex-col', className)}
        data-state={isChecked ? 'checked' : 'unchecked'}
        {...rest}
      >
        {children}
      </div>
    </OptionContextType.Provider>
  );
}

/* -----------------------------------------------------------------------------------------------*/

// TODO: add ability to pass logo/img as prop;
interface DeliveryOptionLabelProps extends React.HTMLAttributes<HTMLLabelElement> {
  /**
   * The name of this delivery option, e.g. "Express shipping"
   */
  optionName: string;
  /**
   * The ID or name of the transporter, used for fetching an organization logo/color
   */
  transporter?: string;
  /**
   * Price in cents
   */
  displayPrice: number;
  /**
   * Optional currency override. If not provided, uses the group context currency.
   */
  currency?: string;
  /**
   * Optional locale override. If not provided, uses the group context locale.
   */
  locale?: string;
  /**
   * Delivery time data used for display
   */
  deliveryTime?: DeliveryTime;
}

/**
 * This component represents the label of a delivery option. When clicked, it will select the delivery option.
 *
 * @remarks
 * It should be used inside a `DeliveryOption` component.
 */
export function DeliveryOptionLabel(props: DeliveryOptionLabelProps) {
  const {
    optionName,
    transporter,
    displayPrice,
    currency,
    deliveryTime,
    locale,
    className,
    ...rest
  } = props;

  const groupContext = use(GroupContext);
  const optionContext = use(OptionContextType);

  const orgAssets = getOrganizationAssets(transporter ?? '');

  // 1) Use the label's locale or fallback to the group's locale
  const finalLocale = locale ?? groupContext.locale ?? 'en';

  // 2) Use the label's currency or fallback to the group's currency
  const finalCurrency = currency ?? groupContext.currency ?? 'EUR';

  // 3) Format the displayed price & delivery time
  const formattedPrice = formatPrice(displayPrice, finalCurrency, finalLocale);
  const formattedDeliveryTime = deliveryTime ? formatDeliveryTime(deliveryTime, finalLocale) : '';

  return (
    <label className={cn('flex flex-row gap-2 cursor-pointer items-baseline', className)} {...rest}>
      <input
        type="radio"
        name={groupContext.name}
        value={optionContext.value}
        checked={optionContext.isChecked}
        onChange={() => groupContext.onValueChange(optionContext.value)}
        className="w-5"
      />

      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between w-full">
          <p className="font-semibold text-black dark:text-white">{optionName}</p>
          <div
            className="w-12 h-7 p-1 rounded-xs"
            style={{
              backgroundColor: orgAssets?.lightColors?.background,
              color: orgAssets?.lightColors?.logo,
            }}
          >
            {orgAssets?.logo && <orgAssets.logo />}
          </div>
        </div>
        <div className="flex items-center justify-start gap-1 opacity-65 text-sm">
          <p>{formattedPrice}</p>
          <p>•</p>
          <p>{formattedDeliveryTime}</p>
        </div>
      </div>
    </label>
  );
}

/* -----------------------------------------------------------------------------------------------*/

interface DeliveryOptionContentProps extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * This component wraps any extra content that is not part of the label
 */
export function DeliveryOptionContent(props: DeliveryOptionContentProps) {
  const { className, ...rest } = props;

  const optionContext = use(OptionContextType);

  return (
    <Collapsible
      className={cn('ml-5 pl-4 flex flex-col', className)}
      open={optionContext.isChecked}
      {...rest}
    />
  );
}

/* -----------------------------------------------------------------------------------------------*/

/**
 * This component will be collapsed when the delivery option is unselected and open when the delivery option is selected.
 *
 * @remarks
 * This component should be used inside a `DeliveryOptionContent` component.
 */
export function DeliveryOptionCollapsibleContent(props: React.HTMLAttributes<HTMLDivElement>) {
  const { ...rest } = props;

  return <CollapsibleContent {...rest} />;
}
